import React, { useEffect } from 'react';
import {
    TaskList,
    Box,
    Grid,
    FaqAccordion,
    LegalDisclaimer,
    VerticalStepper,
    Typography,
} from '@trp-individual-investor-adv-svc/ii-prosp-components';
import { closeModal, commonErrorPromise, log, setLastRequestTime, showModal } from '../../../Utils';
import { dispatchOverlayLoad } from '../Analytics';
import api from '../../../actions/EndPoints';
import { PsBeaconButton } from '../BeaconRegister';
import axiosInstance from '../../../actions/AxiosInstance';
import LoadingSpinner from '../../../containers/LoadingSpinner';
import { summaryCall } from '../../../services/summaryService';
import { useApiServices } from '../../apiServices';
import { USER_PROFILE_FORM_MODAL, PROSP_MODAL_PERSONAL_DETAILS, PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE, PROSP_MODAL_ADD_ACCOUNTS } from '../../../Constants';
import UserProfileForm from '../../modals/UserProfileForm';
import { getRtqData } from '../ApiCalls/ApiCalls';

const NewClient = ({ state, dispatch, profile }) => {
    const { pendingInvite, isCustomerWebView, apiKey, lastCompletedSection, prospStatuses, lastProspSection } =
        state;

    const { getKycInfo, getClientPreferences, getFinAccounts } =
        useApiServices(dispatch);

    const [isLoading, setIsLoading] = React.useState(false);
    const hasCNGN = prospStatuses?.includes('CNGN');
    const [hasConsented, setHasConsented] = React.useState(false);
    const smallScreenSize = window.matchMedia('(max-width: 600px)');
    const [buttonSize, setButtonSize] = React.useState(
        window.innerWidth <= 600 ? '100%' : 'inherit'
    );
    const [getSteps, setSteps] = React.useState([]);

    smallScreenSize.addEventListener('change', function () {
        setButtonSize(smallScreenSize.matches ? '100%' : 'inherit');
    });

    const encodedUrlText = pendingInvite && pendingInvite.encodedUrlText;

    useEffect(() => {
        const getTempSteps = [
            {
                label: 'Enter your personal information',
                description: ``,
                active: prospStatuses?.includes('NEWP') && !prospStatuses?.includes('KYCC'),
                completed: prospStatuses?.includes('KYCC'),
            },
            {
                label: 'Assess your risk tolerance',
                description: '',
                active: prospStatuses?.includes('KYCC') && !prospStatuses?.includes('RTQC'),
                completed: prospStatuses?.includes('RTQC'),
            },
            {
                label: 'Select your accounts',
                description: ``,
                active: prospStatuses?.includes('RTQC') && !prospStatuses?.includes('FPAC'),
                completed: prospStatuses?.includes('FPAC'),
            },
            {
                label: 'Schedule an appointment',
                description: ``,
                active: prospStatuses?.includes('FPAC') && !prospStatuses?.includes('APND'),
                completed: prospStatuses?.includes('APND'),
            },
        ];

        setSteps(getTempSteps);
    }, [state, prospStatuses]);

    const taskList = [
        {
            label: 'Provide basic personal information',
            id: 'provide-personal-info-task',
            description: `Share a few personal and financial details so we can tailor your plan. If you have a co-client, you’ll invite them here.`,
        },
        {
            label: 'Take the risk tolerance questionnaire',
            id: 'rtq-questionnaire-task',
            description: `Answer a few questions about your goals, timeline, and risk tolerance. We will use your answers to personalize your investment recommendations.`,
        },
        {
            label: 'Select accounts to include in your plan',
            id: 'select-accounts-task',
            description: `Add internal and external accounts you own to help us build a more accurate financial plan. Jointly held accounts must be added and approved by a
            co-client.`,
        },
        {
            label: 'Schedule an appointment with a financial advisor',
            id: 'schedule-appointment-task',
            description: `Meet with an advisor to review your goals and bring your plan to life. You will explore your options and decide on next steps together.`,
        },
    ];

    async function updateConsent() {
        try {
            const postConsent = await axiosInstance.post(
                api.consentAPI,
                { engagementType: 'RAS_ONBOARDING' },
                {
                    headers: {
                        apikey: `${apiKey}`,
                    },
                }
            );

            if (postConsent) {
                setLastRequestTime();
                dispatch({
                    type: 'CONSENT_UPDATED',
                    isConsented: true,
                });
                dispatch({
                    type: 'PROSP_STATUSES',
                    prospStatuses: ['NEWP'],
                });

                return postConsent;
            }
            return null;
        } catch (error) {
            // If co-client started invitation flow and status is updated to PLDL / url expired
            closeModal(PROSP_MODAL_PERSONAL_DETAILS)
            throw error;
        }
    }

    const isOnboardingClient = () => {
        return (
            profile?.type === 'ONBOARDING_CLIENT' &&
            profile?.type !== 'CONSENT_GIVEN_CLIENT' &&
            !prospStatuses?.includes('NEWP') &&
            !hasConsented
        );
    };

    const handleInitialFlow = () => {
        if (state?.userStatus?.interventionRequired && !prospStatuses?.includes('NEWP')) {
            showModal(USER_PROFILE_FORM_MODAL);
            dispatchOverlayLoad('User Profile Form Modal');
            return;
        }

        if (state?.userStatusFailure) {
            commonErrorPromise();
            return;
        }

        if (isOnboardingClient()) {
            Promise.resolve()
                .then(() => setIsLoading(true))
                .then(() => updateConsent())
                .then(() => summaryCall(apiKey, dispatch))
                .then(() => getKycInfo(apiKey))
                .then(() => setHasConsented(true))
                .then(() => {
                    showModal(PROSP_MODAL_PERSONAL_DETAILS);
                })
                .then(() => setIsLoading(false))
                .catch((err) => {
                    commonErrorPromise(err);
                    log(console.log(err));
                    setIsLoading(false);
                    closeModal(PROSP_MODAL_PERSONAL_DETAILS);
                });
        } else {
            setIsLoading(true);
            Promise.resolve()
                .then(() => {
                    setIsLoading(false);
                    showModal(PROSP_MODAL_PERSONAL_DETAILS);
                })
                .catch((err) => {
                    commonErrorPromise(err);
                    log(console.log(err));
                    setIsLoading(false);
                });
        }
    };

    const handleKycFlow = () => {
        closeModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE);
        showModal(PROSP_MODAL_PERSONAL_DETAILS);
    };

    const handleRtqFlow = () => {
        closeModal(PROSP_MODAL_ADD_ACCOUNTS);
        showModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE);
    };

    const handleAddAccountsFlow = () => {
        closeModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE);
        showModal(PROSP_MODAL_ADD_ACCOUNTS)
    };

    const shouldHandleKyc = () => {
        return lastCompletedSection === 'newp' && !lastProspSection;
    };

    const shouldHandleRtq = () => {
        return (lastCompletedSection === 'kyc' && !lastProspSection) || lastProspSection === 'kyc' || lastCompletedSection === 'goals';
    };

    const shouldHandleAddAccounts = () => {
        return (
            (lastCompletedSection === 'rtq' && !lastProspSection) ||
            lastProspSection === 'rtq' ||
            (lastCompletedSection === 'add-accounts' && !lastProspSection) ||
            lastProspSection === 'prosp-consent' ||
            lastProspSection === 'prosp-complete' ||
            lastProspSection === 'add-accounts'
        );
    };

    const getOnClick = (e) => {
        e.preventDefault();
        log(console.log(e));

        if (isCustomerWebView || isLoading) {
            return;
        }

        if (!lastCompletedSection && !lastProspSection) {
            handleInitialFlow();
        } else if (shouldHandleKyc()) {
            setIsLoading(true);
            Promise.resolve()
                .then(() => !state?.kycData && getKycInfo(apiKey))
                .then(() => !state?.clientPreferences && getClientPreferences(apiKey))
                .then(() => {
                    setIsLoading(false);
                    handleKycFlow();
                })
                .catch((err) => {
                    commonErrorPromise(err);
                    setIsLoading(false);
                });
        } else if (shouldHandleRtq()) {
            setIsLoading(true);
            Promise.resolve()
                .then(() => !state?.clientPreferences && getClientPreferences(apiKey))
                .then(() => !state?.rtqData && getRtqData(apiKey, dispatch))
                .then(() => {
                    setIsLoading(false);
                    handleRtqFlow();
                })
                .catch((err) => {
                    commonErrorPromise(err);
                    setIsLoading(false);
                });
        } else if (shouldHandleAddAccounts()) {
            setIsLoading(true);
            Promise.resolve()
                .then(() => !state?.financialAccounts && hasCNGN && getFinAccounts(apiKey))
                .then(() => {
                    setIsLoading(false);
                    handleAddAccountsFlow();
                })
                .catch((err) => {
                    commonErrorPromise(err);
                    setIsLoading(false);
                });
        }
    };

    useEffect(() => {
        if (encodedUrlText) {
            showModal('outstanding-coclient-invite');
            dispatchOverlayLoad('Outstanding CoClient Invite Modal');
        }
        sessionStorage.removeItem('emailInv');
    }, [encodedUrlText]);

    useEffect(() => {
        if (
            state.activeActions &&
            state.activeActions.some((action) => action.actionType === 'E_DELIVERY')
        ) {
            const eDeliveryModal = document.querySelector('#e-delivery');
            if (eDeliveryModal !== null) {
                showModal('e-delivery');
                dispatchOverlayLoad('E Delivery Consent Modal');
            }
        }
    }, [state.activeActions]);

    return (
        <>
            <Box
                id="transition-page-container"
                sx={{ paddingTop: { xs: '48px', md: '64px' }, margin: '0 auto' }}
                className="beacon-theme-modern-outlook">
                <Box className="b-container-content" sx={{ inlineSize: 'unset !important' }}>
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            inlineSize: 'unset',
                            justifyContent: ' space-between',
                        }}>
                        <Grid item cols={12} lg={8}>
                            {(!prospStatuses || prospStatuses?.length === 0) && (
                                <Box id="task-list-container" sx={{ paddingRight: { md: '88px' } }}>
                                    <Box
                                        id="task-list-content-container"
                                        sx={{ paddingBottom: '32px' }}>
                                        <Typography
                                            id="taks-list-content-title"
                                            type="headline" serif loose scale={400} bold
                                            sx={{ paddingBottom: '16px' }}>
                                            Get a personalized financial plan
                                        </Typography>
                                        <Typography
                                            id="vertical-stepper-content-subtitle"
                                            type="body" loose scale={200} light>
                                            Start building your free financial plan in just a few steps.
                                            You&apos;ll just need to share some key details so we can
                                            tailor it to you. Your information will be saved
                                            periodically throughout the process.
                                        </Typography>
                                    </Box>
                                    <TaskList taskList={taskList} />
                                    <Box id="start-building-plan-button" sx={{ paddingTop: '32px' }}>
                                        <PsBeaconButton
                                            id="start-building-plan"
                                            data-testid="beacon-button"
                                            variant="primary"
                                            scale="jumbo"
                                            onClick={getOnClick}
                                            disabled={isCustomerWebView}
                                            aria-disabled={isCustomerWebView}
                                            style={{ width: buttonSize }}>
                                            {!isLoading && 'Start Building My Plan'}
                                            {isLoading && (
                                                <LoadingSpinner scale="mini" text="Loading..." />
                                            )}
                                        </PsBeaconButton>
                                    </Box>
                                </Box>
                            )}
                            {prospStatuses?.length > 0 && (
                                <Box id="vertical-stepper-container">
                                    <Box
                                        id="vertical-stepper-content-container"
                                        sx={{ paddingBottom: '48px' }}>
                                        <Typography
                                            id="vertical-stepper-content-title"
                                            type="headline" serif loose scale={400} bold
                                            sx={{ paddingBottom: '16px', width: '100%' }}>
                                            Welcome back!
                                        </Typography>
                                        <Typography
                                            id="vertical-stepper-content-subtitle"
                                            type="body" loose scale={200} light>
                                            We&apos;re one step closer to creating an actionable
                                            financial plan that works for you.
                                        </Typography>
                                    </Box>
                                    <VerticalStepper steps={getSteps} />
                                    <Box id="continue-building-plan-button" sx={{ paddingTop: '32px' }}>
                                        <PsBeaconButton
                                            id="continue-building-plan"
                                            variant="primary"
                                            scale="jumbo"
                                            onClick={getOnClick}
                                            disabled={isCustomerWebView}
                                            aria-disabled={isCustomerWebView}
                                            style={{ width: buttonSize }}>
                                            {!isLoading && 'Continue Building My Plan'}
                                            {isLoading && (
                                                <LoadingSpinner scale="mini" text="Loading..." />
                                            )}
                                        </PsBeaconButton>
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                        <Grid
                            item
                            sx={{
                                paddingLeft: { xs: '0px !important', lg: 'inherit' },
                                paddingTop: { xs: '72px !important', lg: 'inherit' },
                            }}
                            cols={12}
                            lg={4}>
                            <Box id="faq-accordion-container">
                                <FaqAccordion id="faq-accordion" />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Box
                    id="disclaimer-container"
                    sx={{
                        marginTop: '88px',
                        padding: { md: '0 !important' },
                        backgroundColor: 'white',
                        inlineSize: 'unset !important',
                    }}
                    role="complementary">
                    <Box
                        id="disclaimer-content-container"
                        sx={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <LegalDisclaimer bgColor="white" />
                    </Box>
                </Box>
            </Box>
            <UserProfileForm state={state} dispatch={dispatch} fromProsp />
        </>
    );
};

export default NewClient;
